/**
 * E2EE-Context: Hält den Private Key im Memory während der Session.
 * Der Key wird beim Login aus dem verschlüsselten Backup entschlüsselt
 * oder beim ersten Chat-Zugriff neu generiert.
 */
import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { api } from './api';
import {
  generateKeyPair,
  exportPublicKey,
  encryptPrivateKey,
  decryptPrivateKey,
  fromBase64,
  toBase64,
} from './e2ee';

interface E2eeCtx {
  privateKey: CryptoKey | null;
  initialized: boolean;
  /** Initialisiert E2EE mit dem User-Passwort (nach Login) */
  initE2ee: (password: string) => Promise<void>;
  /** Prüft ob E2EE bereit ist */
  isReady: boolean;
}

const Ctx = createContext<E2eeCtx | null>(null);

interface KeyPairResponse {
  keyPair: {
    publicKeyJwk: JsonWebKey;
    encryptedPrivateKey: string;
    privateKeyIv: string;
    privateKeySalt: string;
  } | null;
}

export function E2eeProvider({ children }: { children: ReactNode }) {
  const [privateKey, setPrivateKey] = useState<CryptoKey | null>(null);
  const [initialized, setInitialized] = useState(false);

  const initE2ee = useCallback(async (password: string) => {
    try {
      // 1. Prüfe ob schon ein Schlüsselpaar existiert
      const { keyPair: existing } = await api.get<KeyPairResponse>('/chat/keys/me');

      if (existing) {
        // Entschlüssle den Private Key mit dem Passwort
        const privKey = await decryptPrivateKey(
          fromBase64(existing.encryptedPrivateKey),
          fromBase64(existing.privateKeyIv),
          fromBase64(existing.privateKeySalt),
          password,
        );
        setPrivateKey(privKey);
      } else {
        // Generiere neues Schlüsselpaar
        const keyPair = await generateKeyPair();
        const publicKeyJwk = await exportPublicKey(keyPair.publicKey);
        const { encrypted, iv, salt } = await encryptPrivateKey(keyPair.privateKey, password);

        // Speichere auf dem Server
        await api.post('/chat/keys', {
          publicKeyJwk,
          encryptedPrivateKey: toBase64(encrypted),
          privateKeyIv: toBase64(iv),
          privateKeySalt: toBase64(salt),
        });

        setPrivateKey(keyPair.privateKey);
      }
      setInitialized(true);
    } catch (err) {
      console.error('E2EE init failed:', err);
      // Bei Fehler trotzdem als initialisiert markieren (Chat ohne E2EE nicht möglich)
      setInitialized(true);
    }
  }, []);

  return (
    <Ctx.Provider value={{ privateKey, initialized, initE2ee, isReady: !!privateKey }}>
      {children}
    </Ctx.Provider>
  );
}

export function useE2ee() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useE2ee außerhalb E2eeProvider');
  return ctx;
}
